import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Q3Page = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showNormal, setShowNormal] = useState(false);
  const [showCF, setShowCF] = useState(false);
  const navigate = useNavigate();

  const handleNextClick = () => {
    navigate('/q4');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                <i className="ri-restaurant-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">CF 생리학 연구</h1>
                <p className="text-sm text-gray-600">Life 마니또</p>
              </div>
            </Link>
            <nav className="flex items-center space-x-4">
              <Link to="/q2" className="text-gray-600 hover:text-gray-800 whitespace-nowrap">이전</Link>
              <button onClick={handleNextClick} className="text-orange-600 hover:text-orange-800 font-medium whitespace-nowrap">다음 질문</button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Q3. 왜 영양소(지방, 단백질) 흡수가 어려울까요?
            </h1>
            <p className="text-xl text-orange-600 font-medium">
              끈끈한 점액이 췌장(이자)의 관을 막기 때문입니다.
            </p>
          </div>

          {/* Comparison Diagrams */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Normal Pancreas */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center">
                <i className="ri-check-circle-line mr-2"></i>
                정상 췌장
              </h3>
              <div className="bg-green-50 rounded-lg p-6 mb-4">
                <div className="relative h-64 flex items-center justify-center">
                  {/* Simplified Normal Pancreas System */}
                  <div className="relative w-full h-full">
                    {/* Pancreas */}
                    <div className="absolute top-8 left-8 w-24 h-16 bg-orange-300 rounded-lg border-2 border-orange-400">
                      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-orange-800">
                        췌장
                      </div>
                    </div>
                    
                    {/* Pancreatic Duct - clear pathway */}
                    <div className="absolute top-16 left-32 w-20 h-4 bg-blue-300 rounded-full border border-blue-400">
                      {!showNormal && (
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-blue-800">
                          췌관
                        </div>
                      )}
                      {showNormal && (
                        <>
                          {/* Flowing enzymes */}
                          <div className="absolute top-1 left-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <div className="absolute top-1 left-6 w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                          <div className="absolute top-1 left-10 w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                          <div className="absolute top-1 left-14 w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                        </>
                      )}
                    </div>
                    
                    {/* Small Intestine */}
                    <div className="absolute top-12 right-8 w-32 h-24 bg-pink-200 rounded-2xl border-2 border-pink-300">
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-pink-800">
                        소장
                      </div>
                      {showNormal && (
                        <>
                          {/* Digested nutrients */}
                          <div className="absolute bottom-4 left-4 w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="absolute bottom-4 left-8 w-3 h-3 bg-blue-500 rounded-full"></div>
                          <div className="absolute bottom-4 left-12 w-3 h-3 bg-green-500 rounded-full"></div>
                          <div className="absolute bottom-4 left-16 w-3 h-3 bg-purple-500 rounded-full"></div>
                          <div className="absolute bottom-6 left-6 text-xs font-semibold text-gray-700">흡수됨</div>
                        </>
                      )}
                    </div>
                    
                    {/* Arrow showing flow */}
                    <div className="absolute top-18 left-52 right-40">
                      <i className="ri-arrow-right-line text-green-600 text-2xl"></i>
                    </div>
                    
                    {/* Labels */}
                    {showNormal && (
                      <>
                        <div className="absolute top-4 left-8 text-xs font-semibold text-green-700 bg-white px-2 py-1 rounded shadow-sm">
                          소화효소 분비
                        </div>
                        <div className="absolute bottom-8 right-8 text-xs font-semibold text-green-700 bg-white px-2 py-1 rounded shadow-sm">
                          정상 흡수
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Button */}
              <button
                onClick={() => setShowNormal(!showNormal)}
                className="w-full py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                {showNormal ? '초기 상태로' : '정상 소화 과정 보기'}
              </button>

              {/* Explanation */}
              {showNormal && (
                <div className="mt-6 space-y-3 text-gray-700 animate-fade-in bg-green-50 p-6 rounded-xl">
                  <p className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>췌관이 열려있어 <strong>소화효소</strong>가 소장으로 원활하게 분비됩니다.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>단백질과 지방</strong>이 효소에 의해 잘 분해됩니다.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-green-600 font-bold">✓</span>
                    <span><strong>지용성 비타민</strong>(A, D, E, K)이 정상적으로 흡수됩니다.</span>
                  </p>
                </div>
              )}
            </div>

            {/* CF Patient Pancreas */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center">
                <i className="ri-close-circle-line mr-2"></i>
                CF 환자 췌장
              </h3>
              <div className="bg-red-50 rounded-lg p-6 mb-4">
                <div className="relative h-64 flex items-center justify-center">
                  {/* Simplified CF Pancreas System */}
                  <div className="relative w-full h-full">
                    {/* Pancreas - damaged */}
                    <div className="absolute top-8 left-8 w-24 h-16 bg-red-300 rounded-lg border-2 border-red-400">
                      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-red-800">
                        췌장
                      </div>
                    </div>
                    
                    {/* Pancreatic Duct - blocked */}
                    <div className="absolute top-16 left-32 w-20 h-4 bg-yellow-600 rounded-full border border-yellow-700">
                      {!showCF && (
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-yellow-100">
                          막힌 췌관
                        </div>
                      )}
                      {showCF && (
                        <>
                          {/* Blocked mucus */}
                          <div className="absolute inset-0 bg-yellow-700 rounded-full"></div>
                          <div className="absolute top-0 left-2 w-3 h-2 bg-yellow-800 rounded-full"></div>
                          <div className="absolute top-0 right-2 w-3 h-2 bg-yellow-800 rounded-full"></div>
                          <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xs font-bold text-red-600">X</div>
                        </>
                      )}
                    </div>
                    
                    {/* Small Intestine */}
                    <div className="absolute top-12 right-8 w-32 h-24 bg-pink-200 rounded-2xl border-2 border-pink-300">
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-pink-800">
                        소장
                      </div>
                      {showCF && (
                        <>
                          {/* Undigested nutrients */}
                          <div className="absolute bottom-4 left-4 w-4 h-4 bg-gray-500 rounded border-2 border-gray-600"></div>
                          <div className="absolute bottom-4 left-10 w-4 h-4 bg-gray-500 rounded border-2 border-gray-600"></div>
                          <div className="absolute bottom-4 left-16 w-4 h-4 bg-gray-500 rounded border-2 border-gray-600"></div>
                          <div className="absolute bottom-6 left-6 text-xs font-semibold text-red-700">흡수 안됨</div>
                        </>
                      )}
                    </div>
                    
                    {/* X mark showing blockage */}
                    <div className="absolute top-18 left-52 right-40">
                      <i className="ri-close-line text-red-600 text-2xl"></i>
                    </div>
                    
                    {/* Labels */}
                    {showCF && (
                      <>
                        <div className="absolute top-4 left-8 text-xs font-semibold text-red-700 bg-white px-2 py-1 rounded shadow-sm">
                          효소 분비 차단
                        </div>
                        <div className="absolute bottom-8 right-8 text-xs font-semibold text-red-700 bg-white px-2 py-1 rounded shadow-sm">
                          영양소 흡수 장애
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Button */}
              <button
                onClick={() => setShowCF(!showCF)}
                className="w-full py-4 bg-red-600 text-white rounded-xl font-bold text-lg hover:bg-red-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                {showCF ? '초기 상태로' : 'CF 환자 소화 과정 보기'}
              </button>

              {/* Explanation */}
              {showCF && (
                <div className="mt-6 space-y-3 text-gray-700 animate-fade-in bg-red-50 p-6 rounded-xl">
                  <p className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span><strong>끈끈한 점액</strong>이 췌관을 막아 소화효소가 분비되지 않습니다.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span><strong>단백질과 지방</strong>이 제대로 분해되지 않습니다.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span><strong>지용성 비타민</strong> 흡수가 크게 감소합니다.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>매 식사마다 <strong>소화효소제</strong> 복용이 필요합니다.</span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <p className="text-gray-700 leading-relaxed mb-6 text-lg">
              폐에서와 같은 원리로, 췌장에서 분비되는 액체도 끈끈해집니다. 이 끈끈한 점액이 소화 효소가 장으로 나가는 통로인 '췌장관'을 막아버립니다.
            </p>
          </div>

          {/* Question Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              단백질과 지방이 어디서 분해 흡수되는지 아시나요?
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed text-center">
              췌장에서 나온 소화효소가 단백질과 지방을 분해해 주는 역할을 합니다. 따라서 기름에 녹는 지용성 비타민의 흡수와 단백질의 흡수, 지방의 흡수가 어려워지게 되어 소화가 잘 되지 않아 소화제를 복용하게 됩니다.
            </p>
          </div>

          {/* Results Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-alert-line text-orange-600 mr-3"></i>
              결과: 영양 흡수 장애
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">외분비 췌장 파괴</h3>
                <p className="text-gray-700 leading-relaxed">
                  끈끈한 점액으로 인한 췌장관 폐쇄는 점진적으로 췌장 조직을 손상시킵니다. 이로 인해 소화효소 분비가 현저히 감소합니다.
                </p>
              </div>
              
              <div className="border-l-4 border-red-500 pl-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">필수 영양소 흡수 부전</h3>
                <p className="text-gray-700 leading-relaxed">
                  지방, 단백질, 지용성 비타민(A, D, E, K)의 흡수가 어려워져 성장과 발달에 필요한 영양소가 부족해집니다. 
                  이를 보완하기 위해 매 식사마다 소화효소제를 복용해야 합니다.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Link 
              to="/q2" 
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>이전: Q2</span>
            </Link>
            
            <button 
              onClick={handleNextClick}
              className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap"
            >
              <span>다음: Q4</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Q3Page;
